import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

/**
 * ⚠️ `expo-application` SE PIDE AQUÍ DENTRO, NO ARRIBA DEL TODO. LEE ESTO
 *    ANTES DE «LIMPIARLO» CONVIRTIÉNDOLO EN UN `import` NORMAL.
 *
 * `expo-application/build/ExpoApplication.js` hace, en su nivel superior:
 *
 *     export default requireNativeModule('ExpoApplication');
 *
 * `requireNativeModule` LANZA si ese módulo nativo no está compilado en el
 * APK. Y lanza al EVALUARSE el módulo, no al llamar a ninguna función: un
 * `try/catch` alrededor de `getAndroidId()` no lo atrapa jamás.
 *
 * Por qué importa tanto aquí: a este archivo lo importa `src/app/_layout.tsx`,
 * que es la RAÍZ de la app. Si el `import` revienta, no arranca nada — pantalla
 * en blanco en todos los teléfonos que tengan un APK sin ese módulo. Y esta
 * capa viaja por OTA (`eas update`), que llega a binarios compilados hace
 * semanas, con otro juego de módulos nativos dentro.
 *
 * `expo-application` entra como dependencia de `expo-notifications`, no porque
 * nadie lo pusiera en `package.json`: o sea que está ahí por un camino que
 * puede cambiar sin que nadie lo decida.
 *
 * Pedirlo dentro de la función lo convierte en lo que ya era el resto del
 * diseño: si no está, no hay huella, y «no hay huella» significa «no sé»
 * (devuelve `null`), que es un caso que todo lo de arriba ya contempla.
 *
 * Es el mismo error que costó un FATAL en cada visita a la web con
 * `expo-apple-authentication` (ver src/app/partner.tsx): el guardia protegía
 * la llamada y el fallo estaba en la carga.
 */
function leerIdentificadorDeAndroid(): string {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const Application = require('expo-application');
  return String(Application.getAndroidId() ?? '').trim();
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * RECONOCER EL TELÉFONO, AUNQUE BORREN LA APP Y LA VUELVAN A INSTALAR
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ── POR QUÉ EXISTE ──────────────────────────────────────────────────────
 *
 * Decisión del fundador (3-oct-2026): «conozco lo truqueros que son en mi
 * país: pueden crear múltiples correos para burlar nuestra seguridad y pedir
 * viaje gratis siempre sin pagar. Nuestra aplicación debe reconocer el
 * dispositivo aunque borren la app y la vuelvan a instalar».
 *
 * Hasta hoy NADA ataba una cuenta a un aparato: se buscó `getAndroidId`,
 * `androidId`, `deviceId`, `installationId` y `expo-application` en todo el
 * repositorio y no había ni una línea. Con un correo nuevo se empezaba de cero.
 *
 * ── QUÉ SE USA, Y POR QUÉ NO HIZO FALTA RECOMPILAR EL APK ───────────────
 *
 * `Settings.Secure.ANDROID_ID`, que `expo-application` expone como constante
 * nativa. Ese paquete YA estaba en node_modules con su código Android, así que
 * esta capa no añade una dependencia nueva al `package.json`.
 *
 * ⚠️ CORRECCIÓN (4-oct-2026). Aquí ponía que el autolinking «ya lo mete en el
 * APK — comprobado». NO estaba comprobado: `expo-application` entra como
 * dependencia transitiva de `expo-notifications`, nadie lo puso a mano, y no
 * se pudo confirmar que el módulo nativo esté dentro del APK que el fundador
 * tiene en su teléfono (compilado el 27-sep, seis días antes de que este
 * archivo existiera).
 *
 * Y eso importa porque esta capa viaja por OTA (`eas update`) a binarios
 * viejos. Por eso el paquete se pide DENTRO de la función (ver
 * `leerIdentificadorDeAndroid`) y no con un `import` de arriba: medido que con
 * el import estático, un APK sin ese módulo no arrancaba —ni en Android ni en
 * iOS—, porque el fallo es al CARGAR y ocurre antes del corte por plataforma.
 * `scripts/probar-huella-sin-modulo.cjs` lo prueba en los dos sentidos.
 *
 * ── QUÉ AGUANTA Y QUÉ NO (dicho de frente) ──────────────────────────────
 *
 *   ✅ Desinstalar y volver a instalar — que es justo el truco que se cierra.
 *      Desde Android 8 el valor va atado a (firma del APK + usuario + aparato),
 *      así que mientras el APK lo firme la misma llave, vuelve el mismo número.
 *   ✅ Borrar los datos de la app.
 *   ❌ Restablecimiento de fábrica. No hay forma de evitarlo en Android, y
 *      quien formatee su teléfono por un viaje gratis ya no sale a cuenta.
 *   ❌ Otro usuario o perfil de trabajo del mismo teléfono: otro número.
 *   ❌ iOS. Apple no da un identificador que sobreviva a borrar la app
 *      (`identifierForVendor` se reinicia). Por eso esto devuelve `null` ahí, y
 *      quien llame tiene que decidir qué hacer sin él — NO dar por bueno un
 *      aparato desconocido.
 *   ⚠️ Un APK parcheado puede mandar lo que quiera. Contra eso está App Check,
 *      que ya está en el proyecto: atestigua que quien llama es la app de
 *      verdad, firmada con la llave de verdad.
 *
 * ── POR QUÉ SE MANDA UNA HUELLA Y NO EL NÚMERO ──────────────────────────
 *
 * El ANDROID_ID identifica el aparato de forma permanente. Guardarlo tal cual
 * en la base sería guardar un identificador de hardware de cada persona que usa
 * la app, para siempre, y eso no hace falta para lo que se quiere: solo hace
 * falta saber si DOS cuentas vienen del MISMO sitio.
 *
 * Por eso lo que sale del teléfono es un resumen SHA-256 del número más una
 * sal fija de la app. Sirve exactamente igual para comparar, y si algún día
 * alguien lee esa colección no se lleva el identificador del teléfono de nadie.
 * La sal no es un secreto —viaja en el bundle— y no pretende serlo: su trabajo
 * es que la huella no coincida con la de otra app, no esconder nada.
 */

/**
 * Sal de la app. Que esté a la vista es deliberado: ver la nota de arriba.
 * Si se cambia, TODOS los aparatos pasan a ser desconocidos y los contadores
 * empiezan de cero — o sea que no se cambia salvo que se quiera justo eso.
 */
const SAL = 'gosafely.v1.identidad-de-dispositivo';

/** Copia local, para no volver a calcular el resumen en cada llamada. */
const CLAVE_CACHE = 'gs_huella_dispositivo_v1';

/**
 * SHA-256 a mano.
 *
 * ⚠️ Esto NO es criptografía de propósito general y no debe usarse para nada
 * más. Está aquí por una razón concreta: `expo-crypto` trae código nativo
 * Android, y meter un módulo nativo obliga a recompilar el APK — justo lo que
 * esta capa evita. Son cuarenta líneas y el algoritmo está comprobado contra
 * los vectores oficiales (cadena vacía y "abc").
 *
 * Es el mismo criterio que ya se tomó en `services/appleSignIn.ts` con el nonce.
 */
function sha256(mensaje: string): string {
  const K = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ];
  let h0 = 0x6a09e667, h1 = 0xbb67ae85, h2 = 0x3c6ef372, h3 = 0xa54ff53a;
  let h4 = 0x510e527f, h5 = 0x9b05688c, h6 = 0x1f83d9ab, h7 = 0x5be0cd19;

  // UTF-8 a bytes, sin depender de TextEncoder (que no está en todos los
  // motores de React Native).
  const bytes: number[] = [];
  for (let i = 0; i < mensaje.length; i += 1) {
    let c = mensaje.charCodeAt(i);
    if (c < 0x80) bytes.push(c);
    else if (c < 0x800) bytes.push(0xc0 | (c >> 6), 0x80 | (c & 0x3f));
    else if (c < 0xd800 || c >= 0xe000) bytes.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 0x3f), 0x80 | (c & 0x3f));
    else {
      i += 1;
      c = 0x10000 + (((c & 0x3ff) << 10) | (mensaje.charCodeAt(i) & 0x3ff));
      bytes.push(0xf0 | (c >> 18), 0x80 | ((c >> 12) & 0x3f), 0x80 | ((c >> 6) & 0x3f), 0x80 | (c & 0x3f));
    }
  }

  const bits = bytes.length * 8;
  bytes.push(0x80);
  while (bytes.length % 64 !== 56) bytes.push(0);
  // Longitud en 64 bits. Los 32 altos van en cero: ningún mensaje de aquí
  // llega a 512 MB.
  bytes.push(0, 0, 0, 0, (bits >>> 24) & 0xff, (bits >>> 16) & 0xff, (bits >>> 8) & 0xff, bits & 0xff);

  const w = new Array<number>(64);
  const rotr = (x: number, n: number) => (x >>> n) | (x << (32 - n));

  for (let bloque = 0; bloque < bytes.length; bloque += 64) {
    for (let i = 0; i < 16; i += 1) {
      w[i] =
        (bytes[bloque + i * 4] << 24) |
        (bytes[bloque + i * 4 + 1] << 16) |
        (bytes[bloque + i * 4 + 2] << 8) |
        bytes[bloque + i * 4 + 3];
    }
    for (let i = 16; i < 64; i += 1) {
      const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
      const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) | 0;
    }
    let a = h0, b = h1, c = h2, d = h3, e = h4, f = h5, g = h6, h = h7;
    for (let i = 0; i < 64; i += 1) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (h + S1 + ch + K[i] + w[i]) | 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) | 0;
      h = g; g = f; f = e; e = (d + t1) | 0;
      d = c; c = b; b = a; a = (t1 + t2) | 0;
    }
    h0 = (h0 + a) | 0; h1 = (h1 + b) | 0; h2 = (h2 + c) | 0; h3 = (h3 + d) | 0;
    h4 = (h4 + e) | 0; h5 = (h5 + f) | 0; h6 = (h6 + g) | 0; h7 = (h7 + h) | 0;
  }

  return [h0, h1, h2, h3, h4, h5, h6, h7]
    .map((x) => (x >>> 0).toString(16).padStart(8, '0'))
    .join('');
}

/**
 * La huella de ESTE teléfono, o `null` si no se puede saber.
 *
 * ⚠️ `null` NO significa «aparato nuevo»: significa «no sé». Quien llame tiene
 * que decidir qué hacer con esa duda, y la decisión correcta nunca es regalar
 * lo que se estaba protegiendo.
 *
 * Devuelve `null` en iOS y en web (no hay identificador que aguante), y también
 * si Android devuelve el valor vacío o el conocido 9774d56d682e549c, que es el
 * que soltaban algunos aparatos con fallo de fábrica — millones de teléfonos
 * compartían ese mismo número, así que tratarlo como válido metería a gente sin
 * relación en el mismo saco.
 */
export async function huellaDelDispositivo(): Promise<string | null> {
  if (Platform.OS !== 'android') return null;

  try {
    const guardada = await AsyncStorage.getItem(CLAVE_CACHE);
    if (guardada) return guardada;
  } catch {
    // Sin caché se recalcula: cuesta microsegundos.
  }

  let crudo = '';
  try {
    // Esto atrapa LAS DOS cosas: que el módulo nativo no esté en este APK (el
    // `require` de dentro lanza) y que la lectura falle. Las dos significan lo
    // mismo para quien llama: «no sé cuál es este aparato».
    crudo = leerIdentificadorDeAndroid();
  } catch (e: any) {
    console.warn('[identidad] no se pudo leer el identificador del aparato:', e?.message ?? e);
    return null;
  }

  if (!crudo || crudo === '9774d56d682e549c') return null;

  const huella = sha256(SAL + '|' + crudo);
  try {
    await AsyncStorage.setItem(CLAVE_CACHE, huella);
  } catch {
    // Que no se pueda guardar la copia no impide devolverla.
  }
  return huella;
}
